import { useState } from "react"

import { Button } from "@/components/ui/button"
import { FileUpload } from "@/components/ui/file-upload"
import { HintText, InputBase, Label, TextField } from "@/components/ui/input"
import { NativeSelect } from "@/components/ui/select"
import { TextEditor } from "@/components/ui/text-editor"
import {
  ContentAspectEnum,
  ContentBodyTypeEnum,
  ContentSourceEnum,
} from "@/types"

const ratioOptions = [
  { label: "16:9 (Widescreen HD)", value: ContentAspectEnum.ratio16x9 },
  { label: "4:3 (Standard SD)", value: ContentAspectEnum.ratio4x3 },
  { label: "9:16 (Vertical Mobile/Reels)", value: ContentAspectEnum.ratio9x16 },
  { label: "1:1 (Square - Instagram)", value: ContentAspectEnum.ratio1x1 },
  { label: "4:5 (Vertical Instagram Feed)", value: ContentAspectEnum.ratio4x5 },
]

const sourceOptions = [
  { label: ContentSourceEnum.youtube, value: ContentSourceEnum.youtube },
  { label: ContentSourceEnum.tiktok, value: ContentSourceEnum.tiktok },
  { label: ContentSourceEnum.vimeo, value: ContentSourceEnum.vimeo },
  { label: ContentSourceEnum.instagram, value: ContentSourceEnum.instagram },
  { label: ContentSourceEnum.other, value: ContentSourceEnum.other },
]

// Form value shape for a ContentBody's nested attributes. All fields are
// optional because the server accepts a polymorphic subset depending on
// content_body_type.
export interface ContentBodyAttributes {
  id?: number
  source?: string | null
  caption?: string | null
  duration_seconds?: number | null
  aspect?: string | null
  transcript?: string | null
  body?: string | null
  reading_time?: number | null
  alt_text?: string | null
  image?: File | null
  image_url?: string | null
}

export interface TrackFormData {
  track: {
    notes: string
    tag_ids: number[]
    post_attributes: {
      id?: number
      title: string
      creator: string
      canonical_url: string
      thumbnail_url: string
      content_body_type: ContentBodyTypeEnum
      content_body_attributes: ContentBodyAttributes
      published: boolean
    }
  }
}

// Inertia's useForm `errors` object is typed as `FormDataErrors<TForm>` which
// is deeply structured. In practice controllers emit dot-key string messages,
// so we accept the opaque errors bag and narrow on read below.
type FormErrorBag = Record<string, unknown>

interface ContentBodyFormsProps {
  data: TrackFormData
  setData: (key: "track", value: TrackFormData["track"]) => void
  errors: FormErrorBag
}

const errorMessage = (
  errors: FormErrorBag,
  key: string,
): string | undefined => {
  const value = errors[key]
  if (typeof value === "string") return value
  if (Array.isArray(value) && typeof value[0] === "string") return value[0]
  return undefined
}

const K = {
  caption: "track.post_attributes.content_body_attributes.caption",
  duration_seconds:
    "track.post_attributes.content_body_attributes.duration_seconds",
  transcript: "track.post_attributes.content_body_attributes.transcript",
  body: "track.post_attributes.content_body_attributes.body",
  reading_time: "track.post_attributes.content_body_attributes.reading_time",
  alt_text: "track.post_attributes.content_body_attributes.alt_text",
  image: "track.post_attributes.content_body_attributes.image",
} as const

/**
 * Content Body Forms component.
 * @param data - The form data.
 * @param setData - The setData function.
 * @param errors - The form errors.
 */
const ContentBodyForms = ({ data, setData, errors }: ContentBodyFormsProps) => {
  const contentBodyType = data.track.post_attributes.content_body_type
  const contentBodyAttributes =
    data.track.post_attributes.content_body_attributes

  // Local preview for a freshly-selected file (data URL from FileReader).
  // The server-provided image_url is read directly from form state so we
  // don't need to sync it via useEffect (avoids cascading renders and
  // keeps the "existing image" / "new image" states cleanly separated).
  const [localImagePreview, setLocalImagePreview] = useState<string | null>(
    null,
  )
  const imagePreview =
    contentBodyType === ContentBodyTypeEnum.photo
      ? contentBodyAttributes.image === null
        ? null
        : (localImagePreview ?? contentBodyAttributes.image_url ?? null)
      : null

  const updateContentBodyAttribute = (
    key: string,
    value: ContentBodyAttributes[keyof ContentBodyAttributes],
  ) => {
    setData("track", {
      ...data.track,
      post_attributes: {
        ...data.track.post_attributes,
        content_body_attributes: {
          ...contentBodyAttributes,
          [key]: value,
        },
      },
    })
  }

  // Handlers for image upload (PhotoBody)
  const handleImageSelect = (files: FileList) => {
    const file = files[0]
    if (file) {
      updateContentBodyAttribute("image", file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setLocalImagePreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = () => {
    updateContentBodyAttribute("image", null)
    setLocalImagePreview(null)
  }

  switch (contentBodyType) {
    /**
     * Video Body component.
     */
    case ContentBodyTypeEnum.video: {
      const captionErr = errorMessage(errors, K.caption)
      const durationErr = errorMessage(errors, K.duration_seconds)
      return (
        <>
          {/* Caption - VideoBody */}
          <TextField
            value={contentBodyAttributes.caption ?? ""}
            onChange={(v) => updateContentBodyAttribute("caption", v)}
            isInvalid={!!captionErr}
          >
            <Label>Caption</Label>
            <InputBase size="md" placeholder="Caption" />
            {captionErr && <HintText isInvalid>{captionErr}</HintText>}
          </TextField>

          {/* Source + Aspect Ratio - VideoBody */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <NativeSelect
              label="Source"
              value={contentBodyAttributes.source ?? ""}
              onChange={(e) =>
                updateContentBodyAttribute("source", e.target.value)
              }
              options={sourceOptions}
            />
            <NativeSelect
              label="Aspect Ratio"
              value={contentBodyAttributes.aspect ?? ""}
              onChange={(e) =>
                updateContentBodyAttribute("aspect", e.target.value)
              }
              options={ratioOptions}
            />
          </div>

          {/* Duration / Length - VideoBody (capped, sits alone) */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TextField
              value={
                contentBodyAttributes.duration_seconds
                  ? String(contentBodyAttributes.duration_seconds)
                  : ""
              }
              onChange={(v) =>
                updateContentBodyAttribute(
                  "duration_seconds",
                  v ? parseInt(v, 10) : null,
                )
              }
              isInvalid={!!durationErr}
            >
              <Label>Duration / Length</Label>
              <InputBase size="md" type="number" placeholder="e.g., 180" />
              {durationErr && <HintText isInvalid>{durationErr}</HintText>}
            </TextField>
          </div>
        </>
      )
    }

    /**
     * Audio Body component.
     */
    case ContentBodyTypeEnum.audio: {
      const captionErr = errorMessage(errors, K.caption)
      const durationErr = errorMessage(errors, K.duration_seconds)
      const transcriptErr = errorMessage(errors, K.transcript)
      return (
        <>
          {/* Caption - AudioBody */}
          <TextField
            value={contentBodyAttributes.caption ?? ""}
            onChange={(v) => updateContentBodyAttribute("caption", v)}
            isInvalid={!!captionErr}
          >
            <Label>Caption</Label>
            <InputBase size="md" placeholder="Caption" />
            {captionErr && <HintText isInvalid>{captionErr}</HintText>}
          </TextField>

          {/* Duration + Source - AudioBody */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TextField
              value={
                contentBodyAttributes.duration_seconds
                  ? String(contentBodyAttributes.duration_seconds)
                  : ""
              }
              onChange={(v) =>
                updateContentBodyAttribute(
                  "duration_seconds",
                  v ? parseInt(v, 10) : null,
                )
              }
              isInvalid={!!durationErr}
            >
              <Label>Duration / Length (seconds)</Label>
              <InputBase size="md" type="number" placeholder="e.g., 180" />
              {durationErr && <HintText isInvalid>{durationErr}</HintText>}
            </TextField>

            <NativeSelect
              label="Source"
              value={contentBodyAttributes.source ?? ""}
              onChange={(e) =>
                updateContentBodyAttribute("source", e.target.value)
              }
              options={sourceOptions}
            />
          </div>

          {/* Transcript - AudioBody */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="transcript">Transcript</Label>
            <textarea
              id="transcript"
              value={contentBodyAttributes.transcript ?? ""}
              onChange={(e) =>
                updateContentBodyAttribute("transcript", e.target.value)
              }
              rows={4}
              placeholder="Audio transcript"
              className="bg-primary text-md text-primary ring-primary placeholder:text-placeholder focus:ring-brand w-full resize-y rounded-lg px-3.5 py-3 shadow-xs ring-1 transition duration-100 ease-linear ring-inset focus:ring-2 focus:outline-none"
            />
            {transcriptErr && <HintText isInvalid>{transcriptErr}</HintText>}
          </div>
        </>
      )
    }

    /**
     * Article Body component.
     */
    case ContentBodyTypeEnum.article: {
      const bodyErr = errorMessage(errors, K.body)
      const readingTimeErr = errorMessage(errors, K.reading_time)
      return (
        <>
          {/* Body - ArticleBody */}
          <TextEditor.Root
            content={contentBodyAttributes.body ?? ""}
            onUpdate={({ editor }) => {
              updateContentBodyAttribute("body", editor.getHTML())
            }}
            placeholder="Article body content"
            isInvalid={!!bodyErr}
            inputClassName="min-h-32 resize-y"
          >
            <TextEditor.Label isRequired>Article</TextEditor.Label>
            <TextEditor.Toolbar floating type="simple" />
            <TextEditor.Content />
            {bodyErr && <TextEditor.HintText>{bodyErr}</TextEditor.HintText>}
          </TextEditor.Root>

          {/* Reading Time - ArticleBody (capped, sits alone) */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <TextField
              value={
                contentBodyAttributes.reading_time
                  ? String(contentBodyAttributes.reading_time)
                  : ""
              }
              onChange={(v) =>
                updateContentBodyAttribute(
                  "reading_time",
                  v ? parseInt(v, 10) : null,
                )
              }
              isInvalid={!!readingTimeErr}
            >
              <Label>Reading Time (minutes)</Label>
              <InputBase size="md" type="number" placeholder="e.g., 5" />
              {readingTimeErr && (
                <HintText isInvalid>{readingTimeErr}</HintText>
              )}
            </TextField>
          </div>
        </>
      )
    }

    /**
     * Photo Body component.
     */
    case ContentBodyTypeEnum.photo: {
      const captionErr = errorMessage(errors, K.caption)
      const altTextErr = errorMessage(errors, K.alt_text)
      const imageErr = errorMessage(errors, K.image)
      return (
        <>
          {/* Caption - PhotoBody */}
          <TextField
            value={contentBodyAttributes.caption ?? ""}
            onChange={(v) => updateContentBodyAttribute("caption", v)}
            isInvalid={!!captionErr}
          >
            <Label>Caption</Label>
            <InputBase size="md" placeholder="Caption" />
            {captionErr && <HintText isInvalid>{captionErr}</HintText>}
          </TextField>

          {/* Image - PhotoBody */}
          <TextField
            value={contentBodyAttributes.alt_text ?? ""}
            onChange={(v) => updateContentBodyAttribute("alt_text", v)}
            isInvalid={!!altTextErr}
          >
            <Label>Alt Text</Label>
            <InputBase size="md" placeholder="Alternative text for the image" />
            {altTextErr && <HintText isInvalid>{altTextErr}</HintText>}
          </TextField>
          <div className="flex flex-col gap-1.5">
            <Label>Image</Label>
            {imagePreview ? (
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={imagePreview}
                  alt="Image preview"
                  className="aspect-video w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition hover:opacity-100">
                  <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    onClick={handleRemoveImage}
                  >
                    Change
                  </Button>
                </div>
              </div>
            ) : (
              <FileUpload.Root>
                <FileUpload.DropZone
                  accept="image/*"
                  allowsMultiple={false}
                  hint="PNG, JPG, or JPEG (max. 5MB)"
                  maxSize={5 * 1024 * 1024}
                  onDropFiles={handleImageSelect}
                />
              </FileUpload.Root>
            )}
            {imageErr && <HintText isInvalid>{imageErr}</HintText>}
          </div>
        </>
      )
    }

    default:
      return null
  }
}

export default ContentBodyForms
